import { useMemo } from 'react';
import { useSelector } from 'src/store/react';
import {
  selectCountry,
  selectPathname,
  selectResultsLocation,
} from 'src/store/selectors';
import {
  experimentCountryCodesForOverridingHasExternalJobs,
  experimentPHLocationIdsToBeIndexedInAU,
  experimentPHPathsToBeIndexedInAU,
} from './data';

export const doOverride = (
  currentCountryCode: string,
  currentPathname: string,
  currentResultsLocationId?: number,
): boolean => {
  // override to force indexability for site: `au`, locations within the `experimentPHLocationIdsToBeIndexedInAU`
  const doOverrideForLocation =
    currentResultsLocationId &&
    experimentCountryCodesForOverridingHasExternalJobs.includes(
      currentCountryCode.toLowerCase(),
    ) &&
    experimentPHLocationIdsToBeIndexedInAU.includes(currentResultsLocationId);

  // override to force indexability for site: `au`, matched URLs within the `experimentUrlsForOverridingHasExternalJobs`
  const doOverrideForSelectUrls =
    currentCountryCode.toLowerCase() === 'au' &&
    experimentPHPathsToBeIndexedInAU.includes(currentPathname);

  return doOverrideForLocation || doOverrideForSelectUrls;
};

/**
 * `useOverrideForExperimentPHLocationIdsToBeIndexedInAu` checks if overrides are required for
 * experiment: Index PH locations in AU
 *
 * Indexation Experiment 1 details (specific location test):
 * On the AU site, if locationId matches `Antipolo City` or `Legazpi City` - Make the page indexable
 *
 * Indexation Experiment 2 details (specific url/path test):
 * On the AU site, if the current URL pathname matches one of the paths in the
 * `experimentPHPathsToBeIndexedInAU` list - make the page indexable
 *
 * @returns {
 *   doOverrideHasExternalJobs: boolean,
 * }
 */
const useOverrideForExperimentPHUrlsToBeIndexedInAu = () => {
  const currentCountryCode = useSelector(selectCountry);
  const currentResultsLocationId = useSelector(
    selectResultsLocation,
  )?.locationId;

  const currentPathname = useSelector(selectPathname);

  return useMemo(
    () => ({
      doOverrideHasExternalJobs: doOverride(
        currentCountryCode,
        currentPathname,
        currentResultsLocationId,
      ),
    }),
    [currentCountryCode, currentResultsLocationId, currentPathname],
  );
};

export default useOverrideForExperimentPHUrlsToBeIndexedInAu;
